import { useRef, useState } from 'react'
import { format } from 'date-fns'
import { Download, History, Upload } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { contextDumpUrl } from '@/api/extractors'
import { useExtractorVersions, useUploadExtractor } from '@/hooks/use-extractors'

interface VersionHistoryTabProps {
  phenotypeId: string
}

export function VersionHistoryTab({ phenotypeId }: VersionHistoryTabProps) {
  const { data: versions, isLoading } = useExtractorVersions(phenotypeId)

  return (
    <div className="space-y-4">
      <HandoffToolbar phenotypeId={phenotypeId} />
      {isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-12 bg-muted animate-pulse rounded" />
          ))}
        </div>
      ) : !versions || versions.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <History className="h-10 w-10 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">
              No extractor versions yet. Mark examples and trigger code generation, or download the
              context bundle, write the extractor yourself, and upload it here.
            </p>
          </CardContent>
        </Card>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Version</TableHead>
              <TableHead>Created</TableHead>
              <TableHead>Examples</TableHead>
              <TableHead>Pass Rate</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {versions.map((v) => {
              const passRateDisplay =
                v.testPassRate !== null
                  ? `${Math.round(v.testPassRate * 100)}%`
                  : '—'
              const passRateColor =
                v.testPassRate === 1.0
                  ? 'text-green-600 font-medium'
                  : v.testPassRate !== null
                  ? 'text-red-600 font-medium'
                  : ''

              return (
                <TableRow key={v.id}>
                  <TableCell className="font-mono">v{v.version}</TableCell>
                  <TableCell>
                    {format(new Date(v.createdAt), 'MMM d, yyyy HH:mm')}
                  </TableCell>
                  <TableCell>{v.exampleCount ?? '—'}</TableCell>
                  <TableCell className={passRateColor}>{passRateDisplay}</TableCell>
                  <TableCell>
                    {v.deployed ? (
                      <Badge variant="success">Deployed</Badge>
                    ) : null}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      )}
    </div>
  )
}

/**
 * Manual handoff actions: download the context bundle (phenotype instructions +
 * validated examples) for writing the extractor outside the automated pipeline,
 * and upload hand-written extractor code as a new version.
 */
function HandoffToolbar({ phenotypeId }: { phenotypeId: string }) {
  const [showUpload, setShowUpload] = useState(false)

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="outline" size="sm" asChild>
          <a href={contextDumpUrl(phenotypeId)} download>
            <Download className="h-4 w-4 mr-2" />
            Download Context Bundle
          </a>
        </Button>
        <Button
          variant={showUpload ? 'secondary' : 'outline'}
          size="sm"
          onClick={() => setShowUpload((v) => !v)}
        >
          <Upload className="h-4 w-4 mr-2" />
          Upload Extractor
        </Button>
      </div>
      {showUpload && (
        <UploadExtractorForm
          phenotypeId={phenotypeId}
          onDone={() => setShowUpload(false)}
        />
      )}
    </div>
  )
}

function UploadExtractorForm({
  phenotypeId,
  onDone,
}: {
  phenotypeId: string
  onDone: () => void
}) {
  const upload = useUploadExtractor(phenotypeId)
  const [extractorFileName, setExtractorFileName] = useState<string | null>(null)
  const [detectFileName, setDetectFileName] = useState<string | null>(null)
  const extractorCode = useRef<string | null>(null)
  const detectCode = useRef<string | null>(null)
  const [deploy, setDeploy] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const readFile = async (
    file: File | undefined,
    setName: (name: string | null) => void,
    target: { current: string | null }
  ) => {
    setError(null)
    if (!file) {
      setName(null)
      target.current = null
      return
    }
    target.current = await file.text()
    setName(file.name)
  }

  const handleSubmit = async () => {
    if (!extractorCode.current) {
      setError('Select the extractor file first (e.g. extractor.ts)')
      return
    }
    setError(null)
    try {
      const result = await upload.mutateAsync({
        extractionFunction: extractorCode.current,
        detectionFunction: detectCode.current ?? undefined,
        deploy,
      })
      onDone()
      // eslint-disable-next-line no-alert
      alert(`Uploaded extractor v${result.version ?? '?'}${result.deployed ? ' and deployed it' : ''}.`)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed')
    }
  }

  return (
    <Card>
      <CardContent className="pt-6 space-y-4">
        <p className="text-sm text-muted-foreground">
          Upload extractor code you wrote and tested against the context bundle. It is stored as a
          new version{deploy ? ' and deployed immediately' : ''}.
        </p>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="extractor-file">Extractor (required)</Label>
            <input
              id="extractor-file"
              type="file"
              accept=".ts,.js,.txt"
              className="block w-full text-sm file:mr-3 file:rounded file:border-0 file:bg-muted file:px-3 file:py-1.5 file:text-sm"
              onChange={(e) => readFile(e.target.files?.[0], setExtractorFileName, extractorCode)}
            />
            {extractorFileName && (
              <p className="text-xs text-muted-foreground">Loaded {extractorFileName}</p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="detect-file">Detection function (optional)</Label>
            <input
              id="detect-file"
              type="file"
              accept=".ts,.js,.txt"
              className="block w-full text-sm file:mr-3 file:rounded file:border-0 file:bg-muted file:px-3 file:py-1.5 file:text-sm"
              onChange={(e) => readFile(e.target.files?.[0], setDetectFileName, detectCode)}
            />
            {detectFileName && (
              <p className="text-xs text-muted-foreground">Loaded {detectFileName}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Checkbox
            id="deploy-on-upload"
            checked={deploy}
            onCheckedChange={(checked) => setDeploy(checked === true)}
          />
          <Label htmlFor="deploy-on-upload">Deploy this version immediately</Label>
        </div>
        {error && <p className="text-sm text-red-600">{error}</p>}
        <div className="flex gap-2">
          <Button size="sm" onClick={handleSubmit} disabled={upload.isPending}>
            {upload.isPending ? 'Uploading…' : 'Upload'}
          </Button>
          <Button size="sm" variant="ghost" onClick={onDone} disabled={upload.isPending}>
            Cancel
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
